import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider, useTheme } from "@/context/ThemeContext";
import { Toaster } from "sonner";
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import DashboardPage from "@/pages/DashboardPage";
import TradingPage from "@/pages/TradingPage";
import PositionsPage from "@/pages/PositionsPage";
import AIAssistantPage from "@/pages/AIAssistantPage";
import RoboTradingPage from "@/pages/RoboTradingPage";
import ProfilePage from "@/pages/ProfilePage";
import TradeHistoryPage from "@/pages/TradeHistoryPage";
import ServicesPage from "@/pages/ServicesPage";
import TicketsPage from "@/pages/TicketsPage";
import BankAccountPage from "@/pages/BankAccountPage";
import AdminDashboardPage from "@/pages/admin/AdminDashboardPage";
import AdminUsersPage from "@/pages/admin/AdminUsersPage";
import AdminTradesPage from "@/pages/admin/AdminTradesPage";
import AdminPaymentsPage from "@/pages/admin/AdminPaymentsPage";
import AdminProfilePage from "@/pages/admin/AdminProfilePage";
import AdminKYCPage from "@/pages/admin/AdminKYCPage";
import AdminBrokerPage from "@/pages/admin/AdminBrokerPage";
import AdminDepositsWithdrawalsPage from "@/pages/admin/AdminDepositsWithdrawalsPage";
import AdminPaymentSettingsPage from "@/pages/admin/AdminPaymentSettingsPage";
import AdminLeverageFundsPage from "@/pages/admin/AdminLeverageFundsPage";
import AdminTicketsPage from "@/pages/admin/AdminTicketsPage";
import AdminSimulationPage from "@/pages/admin/AdminSimulationPage";
import AdminCreditPage from "@/pages/admin/AdminCreditPage";
import AdminSpreadPage from "@/pages/admin/AdminSpreadPage";
import ContactUsPage from "@/pages/ContactUsPage";
import AboutPage from "@/pages/AboutPage";
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";
import DepositPage from "@/pages/DepositPage";
import WithdrawPage from "@/pages/WithdrawPage";
import LeverageFundPage from "@/pages/LeverageFundPage";
import AdminSendEmailsPage from "./pages/admin/AdminSendEmailsPage";
import CookieBanner from "./components/ui/cookieBanner";
import DownloadAppPage from "./pages/DownloadAppPage";
import MobileLogin from "./pages/MobileLogin";
import MobileRegister from "./pages/MobileRegister";
import Registration from "./pages/Registration";
import Demo from "./pages/Demo";

function Spinner() {
  return <div className="min-h-screen flex items-center justify-center bg-[var(--bg-primary)]"><div className="w-8 h-8 border-2 border-green-500 border-t-transparent rounded-full animate-spin" /></div>;
}

/** User-only route: logged-in non-admin users */
function UserRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Spinner />;
  if (!user) return <Navigate to="/" />;
  if (user.role === 'admin') return <Navigate to="/admin" />;
  return children;
}

/** Admin-only route: logged-in admin users */
function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Spinner />;
  if (!user) return <Navigate to="/" />;
  if (user.role !== 'admin') return <Navigate to="/dashboard" />;
  return children;
}

/** Public route: redirect logged-in users to their panel */
function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Spinner />;
  if (user) return <Navigate to={user.role === 'admin' ? '/admin' : '/dashboard'} />;
  return children;
}

function ThemedToaster() {
  const { theme } = useTheme();
  return <Toaster position="top-right" theme={theme} richColors />;
}
/** KYC-protected route: only verified users can access */
function KycRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) return <Spinner />;

  if (!user) return <Navigate to="/" />;

  if (user.role === 'admin') return <Navigate to="/admin" />;

  if (user.kyc_status !== 'verified') {
    return <Navigate to="/profile" replace />;
  }

  return children;
}
function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <ThemedToaster />
          <Routes>
            {/* Public */}
            <Route path="/" element={<PublicRoute><LandingPage /></PublicRoute>} />
            <Route path="/login-dashboard" element={<PublicRoute><LoginPage /></PublicRoute>} />
            <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/contact" element={<ContactUsPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/download-app" element={<DownloadAppPage />} />
            <Route path="/registration" element={<Registration />} />
            <Route path="/demo" element={<Demo />} />
<Route
  path="/trade"
  element={
    <KycRoute>
      <TradingPage />
    </KycRoute>
  }
/>

<Route
  path="/robo-trading"
  element={
    <KycRoute>
      <RoboTradingPage />
    </KycRoute>
  }
/>

<Route
  path="/deposit"
  element={
    <KycRoute>
      <DepositPage />
    </KycRoute>
  }
/>

<Route
  path="/withdraw"
  element={
    <KycRoute>
      <WithdrawPage />
    </KycRoute>
  }
/>

<Route
  path="/leverage"
  element={
    <KycRoute>
      <LeverageFundPage />
    </KycRoute>
  }
/>
            {/* User routes */}
            <Route path="/dashboard" element={<UserRoute><DashboardPage /></UserRoute>} />
            
            <Route path="/positions" element={<UserRoute><PositionsPage /></UserRoute>} />
            <Route path="/ai-assistant" element={<UserRoute><AIAssistantPage /></UserRoute>} />
            
         
            <Route path="/history" element={<UserRoute><TradeHistoryPage /></UserRoute>} />
            <Route path="/services" element={<UserRoute><ServicesPage /></UserRoute>} />
         
            <Route path="/tickets" element={<UserRoute><TicketsPage /></UserRoute>} />
            <Route path="/bank-accounts" element={<UserRoute><BankAccountPage /></UserRoute>} />
            <Route path="/profile" element={<UserRoute><ProfilePage /></UserRoute>} />
<Route
  path="/mobile-auth/login"
  element={<UserRoute><MobileLogin /></UserRoute>}
/>

<Route
  path="/mobile-auth/register"
  element={<UserRoute><MobileRegister /></UserRoute>}
/>
            {/* Admin routes - completely separate */}
            <Route path="/admin" element={<AdminRoute><AdminDashboardPage /></AdminRoute>} />
            <Route path="/admin/users" element={<AdminRoute><AdminUsersPage /></AdminRoute>} />
            <Route path="/admin/trades" element={<AdminRoute><AdminTradesPage /></AdminRoute>} />
            <Route path="/admin/payments" element={<AdminRoute><AdminPaymentsPage /></AdminRoute>} />
            <Route path="/admin/kyc" element={<AdminRoute><AdminKYCPage /></AdminRoute>} />
            <Route path="/admin/broker" element={<AdminRoute><AdminBrokerPage /></AdminRoute>} />
            <Route path="/admin/deposits-withdrawals" element={<AdminRoute><AdminDepositsWithdrawalsPage /></AdminRoute>} />
            <Route path="/admin/payment-settings" element={<AdminRoute><AdminPaymentSettingsPage /></AdminRoute>} />
            <Route path="/admin/leverage-funds" element={<AdminRoute><AdminLeverageFundsPage /></AdminRoute>} />
            <Route path="/admin/tickets" element={<AdminRoute><AdminTicketsPage /></AdminRoute>} />
            <Route path="/admin/simulation" element={<AdminRoute><AdminSimulationPage /></AdminRoute>} />
            <Route path="/admin/credit-report" element={<AdminRoute><AdminCreditPage /></AdminRoute>} />
            <Route path="/admin/spread" element={<AdminRoute><AdminSpreadPage /></AdminRoute>} />
            <Route path="/admin/profile" element={<AdminRoute><AdminProfilePage /></AdminRoute>} />
            <Route path="/admin/send-emails" element={<AdminRoute><AdminSendEmailsPage /></AdminRoute>} />

            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
          <CookieBanner />
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
